import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, useAuth, formatApiError } from "@/lib/auth";
import SnapshotImportCard from "@/components/admin/SnapshotImportCard";
import { KeyRound, Loader2, ShieldCheck, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const PinSettingsPage = () => {
  const { user } = useAuth();
  const [hasPin, setHasPin] = useState(false);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/auth/pin/status");
        setHasPin(!!data.has_pin);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const savePin = async (e) => {
    e.preventDefault();
    if (pin.length !== 6) return toast.error("PIN must be 6 digits");
    if (pin !== confirmPin) return toast.error("PINs don't match");
    setBusy(true);
    try {
      await apiClient.post("/auth/pin/set", { pin });
      setHasPin(true);
      setPin("");
      setConfirmPin("");
      toast.success("PIN saved. You can now sign in with your PIN.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removePin = async () => {
    if (!window.confirm("Remove your PIN? You'll need to sign in with your password only.")) return;
    setBusy(true);
    try {
      await apiClient.delete("/auth/pin");
      setHasPin(false);
      toast.success("PIN removed.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] p-6 md:p-10">
      <div className="max-w-2xl mx-auto">
        <Link to="/admin" className="mono-body text-[11px] text-nex-chrome hover:text-nex-gold inline-flex items-center gap-2 mb-6">
          <ArrowLeft size={14} /> Back to Console
        </Link>

        {user?.role === "super_admin" && <SnapshotImportCard />}

        <div className="card-industrial corner-tick p-8 md:p-10">
          <div className="flex items-center gap-3 mb-2">
            <KeyRound className="text-nex-gold" size={22} />
            <div className="mono-label">// PIN QUICK-UNLOCK</div>
          </div>
          <h1 className="serif-head text-3xl mb-2">Set your 6-digit PIN</h1>
          <p className="text-nex-chrome mb-8">
            Signed in as <span className="text-nex-silver">{user?.email}</span>. Once set, you can sign in with just email + PIN instead of your full password. Convenience only — your password remains your primary credential.
          </p>

          {loading ? (
            <div className="text-center py-8"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
          ) : hasPin ? (
            <div data-testid="pin-active" className="space-y-6">
              <div className="flex items-center gap-3 bg-nex-gold/10 border border-nex-gold/40 p-4">
                <ShieldCheck className="text-nex-gold" size={20} />
                <div className="mono-body text-sm text-nex-silver">Your 6-digit PIN is active.</div>
              </div>
              <button
                type="button"
                onClick={removePin}
                disabled={busy}
                data-testid="pin-remove"
                className="tick-btn-secondary text-red-400 hover:text-red-300"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} Remove PIN
              </button>
              <div className="metal-divider" />
              <div>
                <div className="mono-label mb-3">Replace with a new PIN</div>
                <form onSubmit={savePin} className="space-y-4">
                  <input
                    data-testid="pin-new"
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="input-industrial tracking-[0.5em] text-center text-xl"
                    placeholder="••••••"
                  />
                  <input
                    data-testid="pin-new-confirm"
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="input-industrial tracking-[0.5em] text-center text-xl"
                    placeholder="•••••• (confirm)"
                  />
                  <button
                    type="submit"
                    disabled={busy || pin.length !== 6 || confirmPin.length !== 6}
                    data-testid="pin-save"
                    className="tick-btn-primary w-full justify-center disabled:opacity-50"
                  >
                    {busy ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : <>Update PIN <KeyRound size={16} /></>}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <form onSubmit={savePin} className="space-y-6" data-testid="pin-setup-form">
              <div>
                <label className="mono-label block mb-2">6-digit PIN</label>
                <input
                  data-testid="pin-new"
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="input-industrial tracking-[0.5em] text-center text-xl"
                  placeholder="••••••"
                  required
                />
              </div>
              <div>
                <label className="mono-label block mb-2">Confirm PIN</label>
                <input
                  data-testid="pin-new-confirm"
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="input-industrial tracking-[0.5em] text-center text-xl"
                  placeholder="••••••"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={busy || pin.length !== 6 || confirmPin.length !== 6}
                data-testid="pin-save"
                className="tick-btn-primary w-full justify-center disabled:opacity-50"
              >
                {busy ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : <>Save PIN <KeyRound size={16} /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PinSettingsPage;
